/**
 * ユーザー管理用のReact Queryフック
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthHeader } from '@/hooks/useAuth';
import {
  bulkCreateUsersAuthenticated,
  bulkValidateUsersAuthenticated,
  createUserAuthenticated,
  updateUserAuthenticated,
  resetPasswordAuthenticated,
  deleteUserAuthenticated,
  getAppDbUserSettingsAuthenticated,
  toggleHubAccessAuthenticated,
  enableAllHubAccessAuthenticated,
} from '@/lib/api/mise/users';
import type {
  BulkCreateUsersRequest,
  BulkCreateUsersResponse,
  BulkValidateUsersRequest,
  BulkValidateUsersResponse,
  CreateUserRequest,
  CreateUserResponse,
  EnableAllHubAccessResponse,
  ResetPasswordResponse,
  UpdateUserRequest,
} from '@/lib/types/mise/user';
import { companyKeys } from '../keys';
import { CACHE_TIME } from '../client';

/**
 * ユーザー作成用のmutationフック
 */
export function useCreateUser(companyId: string) {
  const { getAuthHeader } = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation<CreateUserResponse, Error, CreateUserRequest>({
    mutationFn: async (request: CreateUserRequest) => {
      const authHeaders = getAuthHeader();
      return createUserAuthenticated(companyId, request, authHeaders);
    },
    onSuccess: () => {
      // ユーザー一覧を再取得
      queryClient.invalidateQueries({
        queryKey: companyKeys.users(companyId),
      });
      // 企業詳細の統計情報も更新
      queryClient.invalidateQueries({
        queryKey: companyKeys.detail(companyId),
      });
    },
  });
}

/**
 * ユーザー更新用のmutationフック
 */
export function useUpdateUser(companyId: string) {
  const { getAuthHeader } = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, request }: { userId: string; request: UpdateUserRequest }) => {
      const authHeaders = getAuthHeader();
      return updateUserAuthenticated(companyId, userId, request, authHeaders);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companyKeys.users(companyId),
      });
      queryClient.invalidateQueries({
        queryKey: companyKeys.detail(companyId),
      });
    },
  });
}

/**
 * appdbのユーザー設定を取得するQuery Hook
 */
export function useAppDbUserSettings(companyId: string, userId: string, accountId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: ['appDbUserSettings', companyId, userId, accountId] as const,
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getAppDbUserSettingsAuthenticated(companyId, userId, accountId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!companyId && !!userId && !!accountId,
    staleTime: CACHE_TIME.ACCOUNT_DETAIL,
  });
}

/**
 * パスワード再発行用のmutationフック
 */
export function useResetPassword(companyId: string) {
  const { getAuthHeader } = useAuthHeader();

  return useMutation<ResetPasswordResponse, Error, string>({
    mutationFn: async (userId: string) => {
      const authHeaders = getAuthHeader();
      return resetPasswordAuthenticated(companyId, userId, authHeaders);
    },
  });
}

/**
 * ユーザー削除用のmutationフック（バックエンドの allow_force_delete=true の環境で動作）
 */
export function useDeleteUser(companyId: string) {
  const { getAuthHeader } = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const authHeaders = getAuthHeader();
      return deleteUserAuthenticated(companyId, userId, authHeaders);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companyKeys.users(companyId),
      });
      queryClient.invalidateQueries({
        queryKey: companyKeys.detail(companyId),
      });
    },
  });
}

/**
 * ユーザー一括登録: バリデーション用 mutation
 */
export function useBulkValidateUsers(companyId: string) {
  const { getAuthHeader } = useAuthHeader();

  return useMutation<BulkValidateUsersResponse, Error, BulkValidateUsersRequest>({
    mutationFn: async (request) => {
      const authHeaders = getAuthHeader();
      return bulkValidateUsersAuthenticated(companyId, request, authHeaders);
    },
  });
}

/**
 * ユーザー一括登録: 実行用 mutation
 */
export function useBulkCreateUsers(companyId: string) {
  const { getAuthHeader } = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation<BulkCreateUsersResponse, Error, BulkCreateUsersRequest>({
    mutationFn: async (request) => {
      const authHeaders = getAuthHeader();
      return bulkCreateUsersAuthenticated(companyId, request, authHeaders);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companyKeys.users(companyId),
      });
      queryClient.invalidateQueries({
        queryKey: companyKeys.detail(companyId),
      });
    },
  });
}

/**
 * StoreHub アクセス ON/OFF 切替用 mutation フック。
 * account 単位で hub_allowed_users (appDB) を更新する。
 */
export function useToggleHubAccess(companyId: string) {
  const { getAuthHeader } = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { email: string; accountId: string; enabled: boolean }>({
    mutationFn: async ({ email, accountId, enabled }) => {
      const authHeaders = getAuthHeader();
      return toggleHubAccessAuthenticated(companyId, email, accountId, enabled, authHeaders);
    },
    onSuccess: () => {
      // ユーザー一覧の hub_access_enabled_account_ids を最新化するため再取得
      queryClient.invalidateQueries({
        queryKey: companyKeys.users(companyId),
      });
    },
  });
}

/**
 * 企業の全ユーザーの StoreHub アクセスを一括で ON にする mutation フック。
 */
export function useEnableAllHubAccess(companyId: string) {
  const { getAuthHeader } = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation<EnableAllHubAccessResponse, Error, void>({
    mutationFn: async () => enableAllHubAccessAuthenticated(companyId, getAuthHeader()),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companyKeys.users(companyId),
      });
    },
  });
}
